/* A net for audio that may never report an end - iOS can silently block play() outside a user
gesture, with no error and no onend, which would hang a sequence forever.

The delay is a *playback budget*, not a wall-clock deadline, and those only agree while the page is
running. A backgrounded page freezes playback but not setTimeout, so a naive timer settles a clip
that never had the chance to finish: the sequence advances while the screen is off and the paused
clip is torn down. So only visible time is spent: every pending net pauses on hide and resumes its
unspent budget on return. Pausing clears the timer, so a callback iOS deferred past the return
cannot settle the clip early. */

const pendingNets = new Set();

document.addEventListener('visibilitychange', () => {
  pendingNets.forEach(net => (document.hidden ? net.pause() : net.resume()));
});

/**
 * @param delayMs playback budget for the clip, including slack
 * @param onFire runs once the page has been visible for delayMs in total
 * @returns {{ arm: function, cancel: function }} arm() restarts the budget, but no-ops once the
 *   net has fired or been cancelled - build a new net per play rather than reusing one
 */
const createAudioSafetyTimer = (delayMs, onFire) => {
  let timer = null;
  let spent = false;
  let remainingMs = delayMs;
  // null while paused; performance.now() because only elapsed time matters
  let runningSince = null;

  const stopTimer = () => {
    clearTimeout(timer);
    timer = null;
    runningSince = null;
  };

  const net = {
    pause: () => {
      if (runningSince !== null) {
        remainingMs = Math.max(remainingMs - (performance.now() - runningSince), 0);
        stopTimer();
      }
    },
    resume: () => {
      if (runningSince === null) {
        runningSince = performance.now();
        timer = setTimeout(() => {
          spent = true;
          stopTimer();
          pendingNets.delete(net);
          onFire();
        }, remainingMs);
      }
    },
  };

  const cancel = () => {
    spent = true;
    stopTimer();
    pendingNets.delete(net);
  };

  const arm = () => {
    if (spent) {
      return;
    }
    stopTimer();
    remainingMs = delayMs;
    pendingNets.add(net);
    if (!document.hidden) {
      net.resume();
    }
  };

  return { arm, cancel };
};

export default createAudioSafetyTimer;
